import {ItemInterface} from '@api/item';
import {getItemByVoucher, getRedeemItemByVoucherJob, redeemItemByVoucher} from '@api/voucher';
import LoadingSpinner from '@components/common/loading-spinner';
import {useApp} from '@components/providers/app-provider';
import resolveImage from '@helpers/resolve-image';
import {CircularProgress} from '@mui/material';
import {checkAttrLevels, checkAttrStats, levelAttributes, mapAttributes} from '@pages/item/[id]';
import {AxiosError} from 'axios';
import {TextEllipsis} from 'neka-simple-utilities';
import Link from 'next/link';
import {useRouter} from 'next/router';
import React, {useEffect, useState} from 'react';
import {HeadLine, ItemsTabs} from '../../components/component';
import Meta from '../../components/common/Meta';
import {UserInterface} from '@api/user';

const Redeem = () => {
  const tab = 'properties';
  const router = useRouter();
  const voucher = router.query.code;
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingRedeem, setIsLoadingRedeem] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean | null>(null);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [imageModal, setImageModal] = useState<boolean>(false);
  const [item, setItem] = useState<ItemInterface | null>(null);
  const [code, setCode] = useState<string>('');
  const [isRedeemed, setIsRedeemed] = useState<boolean>(false);
  const [isFailedRedeem, setIsFailedRedeem] = useState<boolean>(false);
  const [openModalDetail, setOpenModalDetail] = useState<boolean>(false);
  const [openModalSubmit, setOpenModalSubmit] = useState<boolean>(false);
  const {user, jwt: token} = useApp();

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const item = await getItemByVoucher({voucher: code});
      if (item.code === 'ERR_BAD_RESPONSE') {
        setItem(null);
        setIsError(true);
      } else {
        setItem(item);
        setIsError(false);
        setOpenModalDetail(true);
      }
    } catch (error) {
      setItem(null);
      setIsError(true);
      console.log(error);
    }
    setIsLoading(false);
  };

  const handleSubmit = () => {
    setIsSubmit(!isSubmit);
  };

  const handleRedeemed = async () => {
    if (!user?.wallet_address) return;

    setOpenModalDetail(false);
    setOpenModalSubmit(true);
    setIsLoadingRedeem(true);
    let job = await redeemItemByVoucher({voucher: code, receiver: user?.wallet_address});
    if ((job as AxiosError).code == 'ERR_BAD_REQUEST') {
      // notifyInvalidVoucher();
      setIsLoadingRedeem(false);
      setIsFailedRedeem(true);
      return;
    }
    await new Promise((resolve, reject) => {
      const interval = setInterval(async () => {
        job = await getRedeemItemByVoucherJob({jobId: Number(job.id)});
        console.log({job});
        if (job.progress == 200) {
          resolve({interval});
        }
        if (job.failedReason) {
          reject({interval});
        }
      }, 3000);
    })
      .then(async ({interval}: any) => {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        // notifyRedeemItem(true);
        setIsRedeemed(!isRedeemed);
        setIsLoadingRedeem(false);
        clearInterval(interval);
      })
      .catch(({interval}: any) => {
        // notifyRedeemItem(true);
        setIsRedeemed(!isRedeemed);
        setIsLoadingRedeem(false);
        clearInterval(interval);
      });
  };

  const handleChangeCode = (e: {target: {value: React.SetStateAction<string>}}) => {
    setCode(e.target.value);
    // setItem(null);
    setIsSubmit(false);
    setIsError(null);
  };

  useEffect(() => {
    if (code !== '') setIsDisabled(false);
    else setIsDisabled(true);
  }, [code]);

  useEffect(() => {
    if (isSubmit) {
      setIsDisabled(true);
      fetchData();
    }
  }, [isSubmit]);

  useEffect(() => {
    if (voucher) {
      setCode(voucher as string);
      handleSubmit();
    }
  }, [voucher]);

  return (
    <>
      <Meta title="Redeem NFT || Nusa | NFT Marketplace" />
      <section className="relative mt-24 lg:pb-48 pb-24">
        <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
          <img src="/images/gradient_light.jpg" alt="gradient" className="h-full" />
        </picture>

        <div className="container">
          <HeadLine
            text={voucher ? 'Redeem Voucher Code' : 'Input Voucher Code'}
            classes="font-display text-jacarta-700 py-16 text-center text-4xl font-medium dark:text-white"
          />

          {voucher && !isError ? (
            <div className="w-full flex relative justify-center ">
              <div className="text-center border border-green rounded-md py-3 pl-3 pr-12 relative">
                <div>{voucher}</div>

                <img
                  className="h-5 w-5 absolute right-4 top-[50%] translate-y-[-50%]"
                  src={isError ? `/images/icons/icon-error.svg` : `/images/icons/icon-check.svg`}
                  alt="Icon Check"
                />
              </div>
            </div>
          ) : (
            <div className="max-w-[770px] relative mx-auto">
              <input
                type="text"
                id="item-name"
                className={`dark:bg-black-green-2  hover:green-light focus:ring-green-2 focus:border-transparent dark:border-dark-grey dark:placeholder:text-grey w-full rounded-lg py-3 px-3  dark:text-white text-dark-grey placeholder:text-grey placeholder:text-grey/[.70] pr-10 ${
                  !isSubmit ? 'border-light-grey' : isError ? '!border-red' : '!border-green'
                }`}
                placeholder="Input your voucher code"
                required
                value={code}
                onChange={handleChangeCode}
                disabled={isLoading || isLoadingRedeem}
              />
              {isSubmit && isError !== null && (
                <img
                  className="h-5 w-5 absolute right-4 top-[50%] translate-y-[-50%]"
                  src={isError ? `/images/icons/icon-error.svg` : `/images/icons/icon-check.svg`}
                  alt="Icon Check"
                />
              )}
            </div>
          )}

          {isSubmit && isError && (
            <div className="text-xs text-center mt-2">
              Invalid voucher code. Please check your code and try again.
            </div>
          )}

          {(!voucher || isError) && (
            <div className="mt-8 mb-6 text-center">
              <button
                disabled={isDisabled || isLoading || isLoadingRedeem}
                onClick={handleSubmit}
                className={
                  !isDisabled && !isLoading && !isLoadingRedeem
                    ? 'bg-green-gradation-light rounded-full py-3 px-8 text-center font-semibold text-white transition-all border border-transparent'
                    : 'dark:bg-white/[.30] bg-light-grey border dark:border-white rounded-full py-3 px-8 text-center font-semibold dark:text-white transition-all'
                }
              >
                {isLoading ? <LoadingSpinner /> : 'Submit'}
              </button>
            </div>
          )}

          {/* Item */}
          <div className={openModalDetail ? 'block modal fade show ' : 'modal fade hidden'}>
            <div className="modal-dialog max-w-[768px] w-full absolute top-0 pb-14">
              <div className="modal-content !bg-none">
                <div className="modal-header !border-0">
                  <h5 className="modal-title md:text-lg text-sm" id="walletModalLabel">
                    You are going to Redeem an NFT
                  </h5>
                  <button
                    type="button"
                    className={`btn-close`}
                    onClick={() => {
                      setOpenModalDetail(false);
                      setIsDisabled(false);
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      className="fill-dark-navy h-6 w-6 dark:fill-white"
                    >
                      <path fill="none" d="M0 0h24v24H0z" />
                      <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
                    </svg>
                  </button>
                </div>
                {item && (
                  <section className="w-full relative mt-8 px-6 pb-14">
                    <picture className="pointer-events-none absolute inset-0 -z-10 dark:hidden">
                      <img src="/images/gradient_light.jpg" alt="gradient" className="h-full" />
                    </picture>
                    <div className=" mbl:overflow-hidden lg:overflow-visible">
                      {item && (
                        <div className="flex flex-wrap">
                          {/* <!-- Image --> */}
                          <figure className="mb-8 md:flex-shrink-0 md:flex-grow-0 md:basis-auto md:w-1/2 w-full pr-3">
                            <button className=" w-full" onClick={() => setImageModal(true)}>
                              {item?.image && (
                                <img
                                  src={resolveImage(item?.image, '/images/banner-default.png')}
                                  alt={item?.name}
                                  className="h-auto rounded-2xl"
                                />
                              )}
                            </button>

                            {/* <!-- Modal --> */}
                            <div className={imageModal ? 'modal fade show block' : 'modal fade'}>
                              <div className="modal-dialog !my-0 flex h-auto max-w-4xl items-center justify-center">
                                <img
                                  src={resolveImage(item?.image, '/images/banner-default.png')}
                                  alt={item?.name}
                                  className="h-auto rounded-2xl"
                                />
                              </div>

                              <button
                                type="button"
                                className="btn-close absolute top-6 right-6"
                                onClick={() => setImageModal(false)}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  width="24"
                                  height="24"
                                  className="h-6 w-6 fill-white"
                                >
                                  <path fill="none" d="M0 0h24v24H0z" />
                                  <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
                                </svg>
                              </button>
                            </div>
                            {/* <!-- end modal --> */}
                          </figure>

                          {/* <!-- Details --> */}
                          <div className="md:basis-auto md:w-1/2 w-full md:pl-4">
                            {/* <!-- Collection / Likes / Actions --> */}
                            <div className="mb-2 ">
                              {/* <!-- Collection --> */}

                              <div className="flex justify-between items-end gap-x-2">
                                <div className="flex flex-col  ">
                                  <div className="md:block hidden">
                                    <button
                                      disabled={isLoadingRedeem}
                                      onClick={() =>
                                        isRedeemed ? router.push('/') : handleRedeemed()
                                      }
                                      className={
                                        !isLoadingRedeem
                                          ? 'bg-green-gradation-light rounded-full py-3 md:py-4 px-4 md:px-8 text-center font-semibold text-white transition-all text-sm md:text-base'
                                          : 'dark:bg-white/[.30] bg-light-grey border dark:border-white rounded-full py-3 md:py-4 px-4 md:px-8 text-center font-semibold dark:text-white transition-all text-sm md:text-base'
                                      }
                                    >
                                      {isRedeemed ? (
                                        'Back to Home'
                                      ) : isLoadingRedeem ? (
                                        <LoadingSpinner />
                                      ) : (
                                        'Redeem Now'
                                      )}
                                    </button>
                                  </div>
                                  <div className="flex items-center mt-2 justify-between gap-[200px]">
                                    <div>
                                      <Link href={`/collection/${item?.Collection.slug}`}>
                                        <a className="text-green-3 mr-2 text-sm md:text-base font-bold">
                                          {item?.Collection.name}
                                        </a>
                                      </Link>
                                    </div>
                                  </div>
                                  <h1 className="font-display text-dark-navy md:mt-1 text-xl md:text-4xl font-semibold dark:text-white">
                                    {item?.name.length > 50
                                      ? TextEllipsis({
                                          text: item?.name,
                                          position: 'end',
                                          length: 50,
                                        })
                                      : item?.name}
                                  </h1>
                                </div>
                              </div>
                            </div>

                            <div className="mb-2 flex w-full">
                              {/* Owner information if single item */}
                              <div className="flex whitespace-nowrap mbl:whitespace-normal gap-2 ">
                                <div className="dark:text-dark-grey text-xs md:text-sm font-medium tracking-tight">
                                  Created By
                                </div>

                                <Link href={`/user/${item?.Creator?.wallet_address}`}>
                                  <div className="text-green-2 text-xs md:text-sm font-medium tracking-tight hover:cursor-pointer">
                                    {item?.Creator?.username
                                      ? item?.Creator?.username
                                      : TextEllipsis({
                                          text: item?.Creator?.wallet_address as any,
                                          position: 'middle',
                                          length: 4,
                                        })}
                                  </div>
                                </Link>
                              </div>
                            </div>
                            <p className="dark:text-jacarta-300 text-dark-navy break-words">
                              {item?.description}
                            </p>
                          </div>
                          {/* <!-- end details --> */}
                          {/* <!-- Item Details --> */}
                          {checkAttrLevels(item?.attributes) && checkAttrStats(item?.attributes) ? (
                            <div
                              className="w-full grid grid-cols-2
                  dark:bg-dark-grey dark:border-grey  border-jacarta-100 rounded-large bg-white mt-5"
                            >
                              <div className="grid grid-rows-1">
                                {item?.attributes && levelAttributes(item?.attributes)}
                              </div>
                              <div className="grid grid-rows-1 ">
                                {item?.attributes && mapAttributes(item?.attributes)}
                              </div>
                            </div>
                          ) : null}
                        </div>
                      )}
                      <ItemsTabs
                        item={item}
                        tab={tab}
                        redeem
                        user={user as UserInterface}
                        onRefresh={''}
                      />
                    </div>
                  </section>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Float button */}
        {openModalDetail && (
          <div className="fixed bottom-2 z-[97] w-full text-center  block md:hidden">
            <button
              disabled={isLoadingRedeem}
              onClick={() => (isRedeemed ? router.push('/') : handleRedeemed())}
              className={
                !isLoadingRedeem
                  ? 'bg-green-gradation-light rounded-full py-3 md:py-4 px-4 md:px-8 text-center font-semibold text-white transition-all text-sm md:text-base w-[90%] mx-auto'
                  : 'dark:bg-white/[.30] bg-light-grey border dark:border-white rounded-full py-3 md:py-4 px-4 md:px-8 text-center font-semibold dark:text-white transition-all text-sm md:text-base w-[90%] mx-auto'
              }
            >
              {isRedeemed ? 'Back to Home' : isLoadingRedeem ? <LoadingSpinner /> : 'Redeem Now'}
            </button>
          </div>
        )}

        {/* Modal Submit */}
        <div className={openModalSubmit ? 'block modal fade show ' : 'fade modal hidden'}>
          <div className="modal-dialog max-w-[500px] w-full ">
            <div className="modal-content p-8 dark:!bg-dark-navy bg-none relative">
              <div className="flex items-start jusitfy-between w-full mb-10">
                <h5 className="md:text-lg text-sm font-semibold">
                  {isRedeemed
                    ? 'Redeem NFT Success'
                    : isFailedRedeem
                    ? 'Redeem NFT Failed'
                    : 'Please Wait'}
                </h5>
                {!isLoadingRedeem && (
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setOpenModalSubmit(false);
                      if (isRedeemed) router.push(`/item/${item?.id}`);
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M17.912 5.02948L11.999 10.9425L6.08604 5.02948C5.94382 4.89934 5.75688 4.82909 5.56415 4.83336C5.37142 4.83763 5.18777 4.91609 5.05146 5.0524C4.91515 5.18872 4.83669 5.37236 4.83242 5.56509C4.82814 5.75782 4.8984 5.94476 5.02854 6.08698L10.9385 12L5.02704 17.9115C4.95482 17.9803 4.89708 18.0629 4.85723 18.1543C4.81738 18.2458 4.79621 18.3443 4.79498 18.4441C4.79374 18.5438 4.81246 18.6428 4.85004 18.7352C4.88761 18.8277 4.94328 18.9116 5.01378 18.9822C5.08427 19.0528 5.16817 19.1086 5.26053 19.1463C5.3529 19.184 5.45187 19.2029 5.55163 19.2018C5.65139 19.2007 5.74992 19.1797 5.84144 19.1399C5.93296 19.1002 6.01561 19.0426 6.08454 18.9705L11.999 13.059L17.912 18.972C18.0543 19.1021 18.2412 19.1724 18.4339 19.1681C18.6267 19.1638 18.8103 19.0854 18.9466 18.9491C19.0829 18.8127 19.1614 18.6291 19.1657 18.4364C19.1699 18.2436 19.0997 18.0567 18.9695 17.9145L13.0565 12.0015L18.9695 6.08698C19.0418 6.01815 19.0995 5.93558 19.1393 5.84412C19.1792 5.75266 19.2004 5.65416 19.2016 5.5544C19.2028 5.45464 19.1841 5.35564 19.1465 5.26323C19.109 5.17081 19.0533 5.08683 18.9828 5.01624C18.9123 4.94564 18.8284 4.88985 18.736 4.85215C18.6437 4.81444 18.5447 4.79558 18.4449 4.79668C18.3452 4.79777 18.2467 4.8188 18.1551 4.85852C18.0636 4.89824 17.981 4.95586 17.912 5.02798V5.02948Z"
                        fill="#9AA09D"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <div
                className={`w-full text-center min-h-[100px] ${
                  (isRedeemed || isFailedRedeem) && 'hidden'
                }`}
              >
                <CircularProgress className="!text-green-2" />
              </div>
              {isRedeemed && (
                <div className="w-full text-center">
                  <div className="w-[218px] h-[218px] mb-9 mx-auto">
                    <div
                      className="w-full pb-[100%] bg-center bg-no-repeat bg-cover rounded-3xl mx-auto"
                      style={{backgroundImage: `url('${resolveImage(item?.image || '')}')`}}
                    ></div>
                  </div>
                  <div className="text-base mb-9">
                    You just redeem{' '}
                    <span className="font-semibold text-transparent bg-clip-text dark:bg-green-gradation-light bg-green-gradation">
                      {item?.name}
                    </span>
                  </div>
                </div>
              )}
              {isFailedRedeem && (
                <div className="w-full text-center">
                  <div className="w-[218px] h-[218px] mb-9 mx-auto">
                    <div
                      className="w-full pb-[100%] bg-center bg-no-repeat bg-cover rounded-3xl mx-auto"
                      style={{backgroundImage: `url('${resolveImage(item?.image || '')}')`}}
                    ></div>
                  </div>
                  <div className="text-base mb-9">Redeem Failed</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Redeem;
